import React, { useState, useEffect } from 'react';
import { array, bool, func, number, object, string } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm, FormSpy } from 'react-final-form';
import classNames from 'classnames';

import { FormattedMessage, intlShape, injectIntl } from '../../../util/reactIntl';
import { required, bookingDatesRequired, composeValidators } from '../../../util/validators';
import {
  START_DATE,
  END_DATE,
  getStartOf,
  addTime,
  isSameDay,
  isDateSameOrAfter,
  isInRange,
  timeOfDayFromLocalToTimeZone,
  timeOfDayFromTimeZoneToLocal,
  monthIdString,
  initialVisibleMonth,
} from '../../../util/dates';
import {
  LINE_ITEM_DAY,
  LINE_ITEM_NIGHT,
  LISTING_STATE_DRAFT,
  LISTING_STATE_PENDING_APPROVAL,
  TIME_SLOT_TIME,
  propTypes,
} from '../../../util/types';
import { BOOKING_PROCESS_NAME } from '../../../transactions/transaction';
import { types as sdkTypes } from '../../../util/sdkLoader';

import {
  Form,
  IconArrowHead,
  PrimaryButton,
  FieldDateRangeInput,
  H6,
  FieldCheckbox,
  SecondaryButton,
  Modal,
  FieldDateInput,
} from '../../../components';

import EstimatedCustomerBreakdownMaybe from '../EstimatedCustomerBreakdownMaybe';

import css from './BookingDatesForm.module.css';
import { isEmpty } from 'lodash';
import { priceData } from '../../../containers/ListingPage/ListingPage.shared';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../../util/routes';

import { ReactComponent as InfoIcon } from '../../../assets/icons/infoOutline.svg';
import { useConfiguration } from '../../../context/configurationContext';

import { ReactComponent as Checked } from '../../../assets/icons/checked.svg';
import { ReactComponent as NotChecked } from '../../../assets/icons/not-checked.svg';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../../util/urlHelpers';
// import Tooltip from '../../Tooltip/Tooltip';
import { Tooltip } from 'react-tooltip';
import CustomRequestForm from '../CustomRequestForm/CustomRequestForm';
import moment from 'moment';

const TODAY = new Date();
const { Money } = sdkTypes;
const ANIMAL_CONTENT_CREATOR = 'animalContentCreator';

const nextMonthFn = (currentMoment, timeZone, offset = 1) =>
  getStartOf(currentMoment, 'month', timeZone, offset, 'months');
const prevMonthFn = (currentMoment, timeZone, offset = 1) =>
  getStartOf(currentMoment, 'month', timeZone, -1 * offset, 'months');
const endOfRange = (date, dayCountAvailableForBooking, timeZone) =>
  getStartOf(date, 'day', timeZone, dayCountAvailableForBooking - 1, 'days');

/**
 * Return a boolean indicating if given date can be found in an array
 * of tile slots (start dates).
 */
const timeSlotsContain = (timeSlots, date, timeZone) => {
  const foundIndex = timeSlots.findIndex(slot =>
    isInRange(date, slot.attributes.start, slot.attributes.end, 'hour', timeZone)
  );
  return foundIndex > -1;
};

const pickMonthlyTimeSlots = (monthlyTimeSlots, date, timeZone) => {
  const monthId = monthIdString(date, timeZone);
  return monthlyTimeSlots?.[monthId]?.timeSlots || [];
};

/**
 * Find first blocked date between two dates.
 * If none is found, null is returned.
 *
 * @param {Object} monthlyTimeSlots propTypes.timeSlot objects
 * @param {Moment} startDate start date, exclusive
 * @param {Moment} endDate end date, exclusive
 * @param {String} timeZone time zone id
 */
const firstBlockedBetween = (monthlyTimeSlots, startDate, endDate, timeZone) => {
  const firstDate = getStartOf(startDate, 'day', timeZone, 1, 'days');
  if (isSameDay(firstDate, endDate, timeZone)) {
    return null;
  }

  const timeSlots = pickMonthlyTimeSlots(monthlyTimeSlots, firstDate, timeZone);
  const contains = timeSlotsContain(timeSlots, firstDate, timeZone);
  return contains ? firstBlockedBetween(monthlyTimeSlots, firstDate, endDate, timeZone) : firstDate;
};

/**
 * Find last blocked date between two dates.
 * If none is found, null is returned.
 *
 * @param {Array} timeSlots propTypes.timeSlot objects
 * @param {Moment} startDate start date, exclusive
 * @param {Moment} endDate end date, exclusive
 * @param {String} timeZone time zone id
 */
const lastBlockedBetween = (monthlyTimeSlots, startDate, endDate, timeZone) => {
  const previousDate = getStartOf(endDate, 'day', timeZone, -1, 'days');
  if (isSameDay(previousDate, startDate, timeZone)) {
    return null;
  }

  const timeSlots = pickMonthlyTimeSlots(monthlyTimeSlots, previousDate, timeZone);
  return timeSlotsContain(timeSlots, previousDate, timeZone)
    ? lastBlockedBetween(monthlyTimeSlots, startDate, previousDate, timeZone)
    : previousDate;
};

/**
 * Check if a blocked date can be found between two dates.
 *
 * @param {Object} timeSlots propTypes.timeSlot objects
 * @param {String} timeZone time zone id
 * @param {Moment} startDate start date, exclusive
 * @param {Moment} endDate end date, exclusive
 */
const isBlockedBetween = (monthlyTimeSlots, timeZone) => (startDate, endDate) => {
  const startInListingTZ = getStartOf(
    timeOfDayFromLocalToTimeZone(startDate, timeZone),
    'day',
    timeZone
  );
  const endInListingTZ = getStartOf(
    timeOfDayFromLocalToTimeZone(endDate, timeZone),
    'day',
    timeZone
  );
  return !!firstBlockedBetween(monthlyTimeSlots, startInListingTZ, endInListingTZ, timeZone);
};

const isStartDateSelected = (timeSlots, startDate, endDate, focusedInput) => {
  return (
    timeSlots && startDate && (!endDate || focusedInput === END_DATE) && focusedInput !== START_DATE
  );
};

const endDateToPickerDate = (unitType, endDate, timeZone) => {
  const isValid = endDate instanceof Date;
  const isDaily = unitType === LINE_ITEM_DAY;

  if (!isValid) {
    return null;
  } else if (isDaily) {
    // API end dates are exlusive, so we need to shift them with daily
    // booking.
    return getStartOf(endDate, 'day', timeZone, -1, 'days');
  } else {
    return endDate;
  }
};

/**
 * Returns an isOutsideRange function that can be passed to
 * a react-dates DateRangePicker component.
 */
const isOutsideRangeFn = (
  monthlyTimeSlots,
  startDate,
  endDate,
  lineItemUnitType,
  dayCountAvailableForBooking,
  timeZone
) => focusedInput => {
  const endOfAvailableRange = dayCountAvailableForBooking - 1;
  const endOfAvailableRangeDate = getStartOf(TODAY, 'day', timeZone, endOfAvailableRange, 'days');
  const outOfBookableDate = getStartOf(TODAY, 'day', timeZone, endOfAvailableRange + 1, 'days');
  const startOfStartDay = getStartOf(startDate, 'day', timeZone);

  // start date selected, end date missing
  const startDateSelected = isStartDateSelected(monthlyTimeSlots, startDate, endDate, focusedInput);
  const endOfBookableRange = startDateSelected
    ? firstBlockedBetween(monthlyTimeSlots, startDate, outOfBookableDate, timeZone)
    : null;

  if (endOfBookableRange) {
    // end the range so that the booking can end at latest on
    // nightly booking: the day the next booking starts
    // daily booking: the day before the next booking starts
    return day => {
      const timeOfDay = timeOfDayFromLocalToTimeZone(day, timeZone);
      const dayInListingTZ = getStartOf(timeOfDay, 'day', timeZone);
      const lastDayToEndBooking = endDateToPickerDate(
        lineItemUnitType,
        endOfBookableRange,
        timeZone
      );
      return (
        !isDateSameOrAfter(dayInListingTZ, startOfStartDay) ||
        !isDateSameOrAfter(lastDayToEndBooking, dayInListingTZ)
      );
    };
  }

  // end date selected, start date missing
  // -> limit the earliest start date for the booking so that it
  // needs to be after the previous booked date
  const endDateSelected = monthlyTimeSlots && endDate && !startDate && focusedInput !== END_DATE;
  const previousBookedDate = endDateSelected
    ? lastBlockedBetween(monthlyTimeSlots, TODAY, endDate, timeZone)
    : null;

  if (previousBookedDate) {
    return day => {
      const timeOfDay = timeOfDayFromLocalToTimeZone(day, timeZone);
      const dayInListingTZ = getStartOf(timeOfDay, 'day', timeZone);
      const firstDayToStartBooking = getStartOf(previousBookedDate, 'day', timeZone, 1, 'days');
      return (
        !isDateSameOrAfter(dayInListingTZ, firstDayToStartBooking) ||
        !isDateSameOrAfter(endOfAvailableRangeDate, dayInListingTZ)
      );
    };
  }

  // standard isOutsideRange function
  return day => {
    const timeOfDay = timeOfDayFromLocalToTimeZone(day, timeZone);
    const dayInListingTZ = getStartOf(timeOfDay, 'day', timeZone);
    return (
      !isDateSameOrAfter(dayInListingTZ, TODAY) ||
      !isDateSameOrAfter(endOfAvailableRangeDate, dayInListingTZ)
    );
  };
};

// Checks if time slot (propTypes.timeSlot) start time equals a day (moment)
const timeSlotEqualsDay = (timeSlot, day, timeZone) => {
  const isTimeBased = timeSlot?.attributes?.type === TIME_SLOT_TIME;
  return isTimeBased
    ? isInRange(day, timeSlot.attributes.start, timeSlot.attributes.end, undefined, timeZone)
    : false;
};

/**
 * Returns an isDayBlocked function that can be passed to
 * a react-dates DateRangePicker component.
 */
const isDayBlockedFn = (
  monthlyTimeSlots,
  startDate,
  endDate,
  unitType,
  dayCountAvailableForBooking,
  timeZone
) => focusedInput => {
  const endOfAvailableRange = dayCountAvailableForBooking - 1;
  const outOfBookableDate = getStartOf(TODAY, 'day', timeZone, endOfAvailableRange + 1, 'days');
  const timeSlotsOnSelectedMonth = pickMonthlyTimeSlots(monthlyTimeSlots, startDate, timeZone);

  // Start date selected/focused, end date missing
  const startDateSelected = isStartDateSelected(
    timeSlotsOnSelectedMonth,
    startDate,
    endDate,
    focusedInput
  );

  // Find the end of bookable range after a start date
  const endOfBookableRange = startDateSelected
    ? firstBlockedBetween(monthlyTimeSlots, startDate, outOfBookableDate, timeZone)
    : null;

  // end date is focused but no dates are selected
  const selectingEndDate =
    timeSlotsOnSelectedMonth &&
    !startDate &&
    !endDate &&
    focusedInput === END_DATE &&
    unitType === LINE_ITEM_NIGHT;

  if (selectingEndDate) {
    // if end date is being selected first, block the day after a booked date
    // (as a booking can end on the day the following booking starts)
    return day => {
      const timeOfDay = timeOfDayFromLocalToTimeZone(day, timeZone);
      const dayInListingTZ = getStartOf(timeOfDay, 'day', timeZone);
      const timeSlots = pickMonthlyTimeSlots(monthlyTimeSlots, dayInListingTZ, timeZone);
      return !timeSlots.find(timeSlot => timeSlotEqualsDay(timeSlot, dayInListingTZ));
    };
  } else if (endOfBookableRange || !timeSlotsOnSelectedMonth) {
    // a next bookable range is found or time slots are not provided
    // -> booking range handles blocking dates
    return () => false;
  } else {
    // otherwise return standard timeslots check
    return day => {
      const timeOfDay = timeOfDayFromLocalToTimeZone(day, timeZone);
      const dayInListingTZ = getStartOf(timeOfDay, 'day', timeZone);
      const timeSlots = pickMonthlyTimeSlots(monthlyTimeSlots, dayInListingTZ, timeZone);
      return !timeSlots.find(timeSlot => timeSlotEqualsDay(timeSlot, dayInListingTZ));
    };
  }
};

const fetchMonthData = (
  date,
  listingId,
  dayCountAvailableForBooking,
  timeZone,
  onFetchTimeSlots
) => {
  const endOfRangeDate = endOfRange(TODAY, dayCountAvailableForBooking, timeZone);

  // Don't fetch timeSlots for past months or too far in the future
  if (isInRange(date, TODAY, endOfRangeDate)) {
    // Use "today", if the first day of given month is in the past
    const start = isDateSameOrAfter(TODAY, date) ? TODAY : date;

    // Use endOfRangeDate, if the first day of the next month is too far in the future
    const nextMonthDate = nextMonthFn(date, timeZone);
    const end = isDateSameOrAfter(nextMonthDate, endOfRangeDate)
      ? getStartOf(endOfRangeDate, 'day', timeZone)
      : nextMonthDate;

    // Fetch time slots for given time range
    onFetchTimeSlots(listingId, start, end, timeZone);
  }
};

const handleMonthClick = (
  currentMonth,
  monthlyTimeSlots,
  fetchMonthData,
  dayCountAvailableForBooking,
  timeZone,
  listingId,
  onFetchTimeSlots
) => monthFn => {
  // Callback function after month has been updated.
  // react-dates component has next and previous months ready (but inivisible).
  // we try to populate those invisible months before user advances there.
  fetchMonthData(
    monthFn(currentMonth, timeZone, 2),
    listingId,
    dayCountAvailableForBooking,
    timeZone,
    onFetchTimeSlots
  );

  // If previous fetch for month data failed, try again.
  const monthId = monthIdString(currentMonth, timeZone);
  const currentMonthData = monthlyTimeSlots[monthId];
  if (currentMonthData && currentMonthData.fetchTimeSlotsError) {
    fetchMonthData(
      currentMonth,
      listingId,
      dayCountAvailableForBooking,
      timeZone,
      onFetchTimeSlots
    );
  }
};

// In case start or end date for the booking is missing
// focus on that input, otherwise continue with the
// default handleSubmit function.
const handleFormSubmit = (setFocusedInput, onSubmit, searchParams) => e => {
  const isAdvanceSelected = searchParams.p === 'advance';
  const isPremiumSelected = searchParams.p === 'premium';
  const isBasicSelected = !(isAdvanceSelected || isPremiumSelected);

  const { startDate, endDate } = e.bookingDates || {};
  if (!startDate) {
    e.preventDefault();
    setFocusedInput(START_DATE);
  } else if (!endDate) {
    e.preventDefault();
    setFocusedInput(END_DATE);
  } else {
    onSubmit({
      ...e,
      selectedPackage: isBasicSelected
        ? 'basic'
        : isAdvanceSelected
        ? 'advance'
        : isPremiumSelected
        ? 'premium'
        : null,
    });
  }
};

// Function that can be passed to nested components
// so that they can notify this component when the
// focused input changes.
const handleFocusedInputChange = setFocusedInput => focusedInput => {
  setFocusedInput(focusedInput);
};

// When the values of the form are updated we need to fetch
// lineItems from this Template's backend for the EstimatedTransactionMaybe
// In case you add more fields to the form, make sure you add
// the values here to the orderData object.
const handleFormSpyChange = (
  listingId,
  isOwnListing,
  fetchLineItemsInProgress,
  onFetchTransactionLineItems,
  searchParams,
  updatePriceAsMoney,
  extraAddOnForCustomList
) => formValues => {
  const { startDate, endDate } =
    formValues.values && formValues.values.bookingDates ? formValues.values.bookingDates : {};

  const { extraAddOn = [], extraAddOnForCustom = [] } =
    formValues.values && formValues.values ? formValues.values : {};

  const isAdvanceSelected = searchParams.p === 'advance';
  const isPremiumSelected = searchParams.p === 'premium';
  const isBasicSelected = !(isAdvanceSelected || isPremiumSelected);

  if (startDate && endDate && !fetchLineItemsInProgress) {
    onFetchTransactionLineItems({
      orderData: {
        bookingStart: startDate,
        bookingEnd: endDate,
        extraAddOn,
        extraAddOnForCustom,
        selectedPackage: isBasicSelected
          ? 'basic'
          : isAdvanceSelected
          ? 'advance'
          : isPremiumSelected
          ? 'premium'
          : null,
        updatedPrice: updatePriceAsMoney,
        extraAddOnForCustomList: extraAddOnForCustomList,
      },
      listingId,
      isOwnListing,
    });
  }
};

// IconArrowHead component might not be defined if exposed directly to the file.
// This component is called before IconArrowHead component in components/index.js
const PrevIcon = props => (
  <IconArrowHead {...props} direction="left" rootClassName={css.arrowIcon} />
);
const NextIcon = props => (
  <IconArrowHead {...props} direction="right" rootClassName={css.arrowIcon} />
);

const Next = props => {
  const { currentMonth, dayCountAvailableForBooking, timeZone } = props;
  const nextMonthDate = nextMonthFn(currentMonth, timeZone);

  return isDateSameOrAfter(
    nextMonthDate,
    endOfRange(TODAY, dayCountAvailableForBooking, timeZone)
  ) ? null : (
    <NextIcon />
  );
};
const Prev = props => {
  const { currentMonth, timeZone } = props;
  const prevMonthDate = prevMonthFn(currentMonth, timeZone);
  const currentMonthDate = getStartOf(TODAY, 'month', timeZone);

  return isDateSameOrAfter(prevMonthDate, currentMonthDate) ? <PrevIcon /> : null;
};

export const BookingDatesFormComponent = props => {
  const [focusedInput, setFocusedInput] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(getStartOf(TODAY, 'month', props.timeZone));

  const [showFullDescription, setShowFullDescription] = useState({
    basic: false,
    advance: false,
    premium: false,
  });

  const toggleDescription = packageType => {
    setShowFullDescription(prevState => ({
      ...prevState,
      [packageType]: !prevState[packageType],
    }));
  };

  useEffect(() => {
    // Call onMonthChanged function if it has been passed in among props.
    if (props.onMonthChanged) {
      props.onMonthChanged(monthId);
    }
  }, [currentMonth]);

  const {
    rootClassName,
    className,
    price: unitPrice,
    listingId,
    isOwnListing,
    fetchLineItemsInProgress,
    onFetchTransactionLineItems,
    onSubmit,
    timeZone,
    dayCountAvailableForBooking,
    marketplaceName,
    searchParams = {},
    history,
    listingSlug,
    listingState,
    updatePriceAsMoney,
    updatedOffering,
    location,
    isAuthenticated,
    ...rest
  } = props;
  const classes = classNames(rootClassName || css.root, className);

  const isAdvanceSelected = searchParams.p === 'advance';
  const isPremiumSelected = searchParams.p === 'premium';
  const isBasicSelected = !(isAdvanceSelected || isPremiumSelected);

  const route = useRouteConfiguration();
  const config = useConfiguration();

  const onFormSubmit = handleFormSubmit(setFocusedInput, onSubmit, searchParams);
  const onFocusedInputChange = handleFocusedInputChange(setFocusedInput);

  const {
    addons: addonsForCustom,
    description: descriptionForCustom,
    extraAddons: extraAddOnForCustom,
  } = updatedOffering || {};

  const onFormSpyChange = handleFormSpyChange(
    listingId,
    isOwnListing,
    fetchLineItemsInProgress,
    onFetchTransactionLineItems,
    searchParams,
    updatePriceAsMoney,
    extraAddOnForCustom
  );

  const ALLOWED_ADD_ON_FOR_OTHERS = config.listing.allowedAddonForOthers || [];
  const ALLOWED_ADD_ON_FOR_CONTENT_CREATORS = config.listing.allowedAddOnForContentCreator || [];

  const createListingURL = queryParams => {
    const isPendingApproval = listingState === LISTING_STATE_PENDING_APPROVAL;
    const isDraft = listingState === LISTING_STATE_DRAFT;
    const variant = isDraft
      ? LISTING_PAGE_DRAFT_VARIANT
      : isPendingApproval
      ? LISTING_PAGE_PENDING_APPROVAL_VARIANT
      : null;

    const linkProps =
      isPendingApproval || isDraft
        ? {
            name: 'ListingPageVariant',
            params: {
              id: listingId.uuid,
              slug: listingSlug,
              variant,
            },
          }
        : {
            name: 'ListingPage',
            params: { id: listingId.uuid, slug: listingSlug },
          };

    return createResourceLocatorString(linkProps.name, route, linkProps.params, {
      ...searchParams,
      ...queryParams,
    });
  };

  return (
    <FinalForm
      {...rest}
      unitPrice={unitPrice}
      onSubmit={onFormSubmit}
      render={fieldRenderProps => {
        const {
          endDatePlaceholder,
          startDatePlaceholder,
          formId,
          handleSubmit,
          intl,
          lineItemUnitType,
          values,
          monthlyTimeSlots,
          lineItems,
          fetchLineItemsError,
          onFetchTimeSlots,
          listingPublicData,
          onManageDisableScrolling,
          listingTitle,
          authorDisplayName,
          isCustomQuoteRequestFormOpen,
          setIsCustomQuoteRequestFormOpen,
          onSubmitCustomQuoteRequest,
          customQuoteRequestInProgress,
          customQuoteRequestError,
          shouldShowAddon,
          shouldShowExtraAddon,
          shouldShowPackage,
          shouldShowCustomQuoteRequestModal,
          shouldShowUpdatedPrice,
          form,
          listing,
        } = fieldRenderProps;

        const dateFormattingOptions = {
          month: 'short',
          day: 'numeric',
          weekday: 'short',
        };

        const selectDateLabel = intl.formatMessage({
          id: 'BookingDatesForm.selectDateLabel',
        });

        const singleDateChange = (value, form, props) => {
          const { timeZone, intl } = props;
          const preparedStartDate = getStartOf(
            timeOfDayFromLocalToTimeZone(
              moment(value.date)
                .startOf('day')
                .toDate(),
              timeZone
            ),
            'day',
            timeZone
          );

          const preparedEndDate = getStartOf(
            timeOfDayFromLocalToTimeZone(
              moment(value.date)
                .clone()
                .add(1, 'days')
                .startOf('day')
                .toDate(),
              timeZone
            ),
            'day',
            timeZone
          );

          form.batch(() => {
            form.change('bookingDates', {
              startDate: preparedStartDate,

              endDate: preparedEndDate,
            });
          });

          // setBookingStartDate(preparedStartDate);
          // setBookingEndDate(preparedEndDate);
        };

        const {
          packageDetail = {},
          packageAndOffer,
          category,
          extraAddons = [],
        } = listingPublicData;
        const { basic = {}, advance = {}, premium = {} } = packageDetail;
        const { startDate, endDate } = values && values.bookingDates ? values.bookingDates : {};

        const isContentCreartor = category === ANIMAL_CONTENT_CREATOR;

        const addonToDisplay = isContentCreartor
          ? ALLOWED_ADD_ON_FOR_CONTENT_CREATORS
          : ALLOWED_ADD_ON_FOR_OTHERS;

        const startDateErrorMessage = intl.formatMessage({
          id: 'FieldDateRangeInput.invalidStartDate',
        });
        const endDateErrorMessage = intl.formatMessage({
          id: 'FieldDateRangeInput.invalidEndDate',
        });

        // This is the place to collect breakdown estimation data.
        // Note: lineItems are calculated and fetched from this Template's backend
        // so we need to pass only booking data that is needed otherwise
        // If you have added new fields to the form that will affect to pricing,
        // you need to add the values to handleOnChange function
        const breakdownData =
          startDate && endDate
            ? {
                startDate,
                endDate,
              }
            : null;

        const showEstimatedBreakdown =
          breakdownData && lineItems && !fetchLineItemsInProgress && !fetchLineItemsError;

        const dateFormatOptions = {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        };

        const startOfToday = getStartOf(TODAY, 'day', timeZone);
        const tomorrow = addTime(startOfToday, 1, 'days');
        const startDatePlaceholderText =
          startDatePlaceholder || intl.formatDate(startOfToday, dateFormatOptions);
        const endDatePlaceholderText =
          endDatePlaceholder || intl.formatDate(tomorrow, dateFormatOptions);

        const onMonthClick = handleMonthClick(
          currentMonth,
          monthlyTimeSlots,
          fetchMonthData,
          dayCountAvailableForBooking,
          timeZone,
          listingId,
          onFetchTimeSlots
        );
        const isDayBlocked = isDayBlockedFn(
          monthlyTimeSlots,
          startDate,
          endDate,
          lineItemUnitType,
          dayCountAvailableForBooking,
          timeZone
        );
        const isOutsideRange = isOutsideRangeFn(
          monthlyTimeSlots,
          startDate,
          endDate,
          lineItemUnitType,
          dayCountAvailableForBooking,
          timeZone
        );

        const renderDescription = (description, packageType) => {
          const truncatedDescription = description
            ?.split(' ')
            .slice(0, 30)
            .join(' ');
          const showMoreButton = description?.split(' ').length > 30;

          return (
            <div className={css.description}>
              {showFullDescription[packageType] ? description : truncatedDescription}
              {showMoreButton && (
                <span onClick={() => toggleDescription(packageType)}>
                  {showFullDescription[packageType] ? 'Show less' : 'Show more'}
                </span>
              )}
            </div>
          );
        };

        const renderCustomOfferDescription = description => {
          const truncatedDescription = description
            ?.split(' ')
            .slice(0, 30)
            .join(' ');
          const showMoreButton = description?.split(' ').length > 30;

          return (
            <div className={css.description}>
              {showFullDescription ? description : truncatedDescription}
              {showMoreButton && (
                <span onClick={() => setShowFullDescription(prevState => !prevState)}>
                  {showFullDescription ? 'Show less' : 'Show more'}
                </span>
              )}
            </div>
          );
        };

        const tooltipMsg =
          'Animal Talent is booked on a day rate, select the date your job will be completed on or the number of days the job will be performed. IE. 2 days selected will be charged double the rate.';

        return (
          <Form onSubmit={handleSubmit} className={classes} enforcePagePreloadFor="CheckoutPage">
            <FormSpy subscription={{ values: true }} onChange={onFormSpyChange} />
            <input type="hidden" className="gclid" placeholder="gclid" name="gclid"></input>

            {shouldShowPackage && (
              <div className={css.packageHeader}>
                <div
                  className={classNames(css.packageName, isBasicSelected && css.selected)}
                  onClick={() => {
                    history.push(createListingURL({ p: 'basic' }));
                  }}
                >
                  Basic
                </div>
                {!isEmpty(advance) && (
                  <div
                    className={classNames(css.packageName, isAdvanceSelected && css.selected)}
                    onClick={() => {
                      history.push(createListingURL({ p: 'advance' }));
                    }}
                  >
                    Advanced
                  </div>
                )}
                {!isEmpty(premium) && (
                  <div
                    className={classNames(css.packageName, isPremiumSelected && css.selected)}
                    onClick={() => {
                      history.push(createListingURL({ p: 'premium' }));
                    }}
                  >
                    Premium
                  </div>
                )}
              </div>
            )}
            {shouldShowUpdatedPrice ? (
              <div className={css.priceContainer}>
                <div className={css.pckgNm}>
                  {/* <FormattedMessage id="BookingDatesForm.customQuotePrice" /> */}
                  {intl.formatMessage({ id: 'BookingDatesForm.customQuotePrice' })}
                </div>
                <div className={css.price}>
                  {priceData(updatePriceAsMoney, updatePriceAsMoney?.currency, intl).formattedPrice}
                  <span className={css.unit}>
                    {isContentCreartor ? (
                      <FormattedMessage id="OrderBreakdown.pricePerJob" />
                    ) : (
                      <FormattedMessage id="OrderBreakdown.pricePerDay" />
                    )}
                  </span>
                  <span className={css.iconContainer}>
                    <a data-tooltip-id="priceTip" data-tooltip-content={tooltipMsg}>
                      <InfoIcon />
                    </a>
                    <Tooltip id="priceTip" className={css.tooltip} />
                  </span>
                </div>
              </div>
            ) : (
              <div className={css.priceContainer}>
                {isBasicSelected && basic && basic.price && (
                  <>
                    <div className={css.pckgNm}>Basic</div>
                    <div className={css.price}>
                      {
                        priceData(
                          new Money(basic?.price?.amount, basic?.price?.currency),
                          basic?.price?.currency,
                          intl
                        ).formattedPrice
                      }
                      <span className={css.unit}>
                        {isContentCreartor ? (
                          <FormattedMessage id="OrderBreakdown.pricePerJob" />
                        ) : (
                          <FormattedMessage id="OrderBreakdown.pricePerDay" />
                        )}
                      </span>
                      <span className={css.iconContainer}>
                        <a data-tooltip-id="priceTip" data-tooltip-content={tooltipMsg}>
                          <InfoIcon />
                        </a>
                        <Tooltip id="priceTip" className={css.tooltip} />
                      </span>
                    </div>
                  </>
                )}
                {isAdvanceSelected && advance && advance.price && (
                  <>
                    <div className={css.pckgNm}>Advanced</div>
                    <div className={css.price}>
                      {
                        priceData(
                          new Money(advance.price.amount, advance.price.currency),
                          advance.price.currency,
                          intl
                        ).formattedPrice
                      }
                      <span className={css.unit}>
                        {isContentCreartor ? (
                          <FormattedMessage id="OrderBreakdown.pricePerJob" />
                        ) : (
                          <FormattedMessage id="OrderBreakdown.pricePerDay" />
                        )}
                      </span>
                      <span className={css.iconContainer}>
                        <a data-tooltip-id="priceTip" data-tooltip-content={tooltipMsg}>
                          <InfoIcon />
                        </a>
                        <Tooltip id="priceTip" className={css.tooltip} />
                      </span>
                    </div>
                  </>
                )}
                {isPremiumSelected && premium && premium.price && (
                  <>
                    <div className={css.pckgNm}>Premium</div>
                    <div className={css.price}>
                      {
                        priceData(
                          new Money(premium.price.amount, premium.price.currency),
                          premium.price.currency,
                          intl
                        ).formattedPrice
                      }
                      <span className={css.unit}>
                        {isContentCreartor ? (
                          <FormattedMessage id="OrderBreakdown.pricePerJob" />
                        ) : (
                          <FormattedMessage id="OrderBreakdown.pricePerDay" />
                        )}
                      </span>
                      <span className={css.iconContainer}>
                        <a data-tooltip-id="priceTip" data-tooltip-content={tooltipMsg}>
                          <InfoIcon />
                        </a>
                        <Tooltip id="priceTip" className={css.tooltip} />
                      </span>
                    </div>
                  </>
                )}
              </div>
            )}

            {shouldShowPackage ? (
              <div className={css.descriptionContainer}>
                {isBasicSelected && renderDescription(basic.description, 'basic')}
                {isAdvanceSelected && renderDescription(advance.description, 'advance')}
                {isPremiumSelected && renderDescription(premium.description, 'premium')}
              </div>
            ) : (
              <div className={css.descriptionContainer}>
                {descriptionForCustom && renderCustomOfferDescription(descriptionForCustom)}
              </div>
            )}

            {shouldShowAddon
              ? addonToDisplay.map(({ label, key, value }) => (
                  <div className={css.pckgRow} key={key}>
                    {isBasicSelected && (
                      <div className={classNames(css.pckg, css.iconContainer)}>
                        {basic?.addonData?.includes(key) ? (
                          <span>
                            <Checked /> {label}
                          </span>
                        ) : (
                          <span>
                            <NotChecked /> {label}
                          </span>
                        )}
                      </div>
                    )}
                    {isAdvanceSelected && (
                      <div className={classNames(css.pckg, css.iconContainer)}>
                        {advance?.addonData?.includes(key) ? (
                          <span>
                            <Checked /> {label}
                          </span>
                        ) : (
                          <span>
                            <NotChecked /> {label}
                          </span>
                        )}
                      </div>
                    )}
                    {isPremiumSelected && (
                      <div className={classNames(css.pckg, css.iconContainer)}>
                        {premium?.addonData?.includes(key) ? (
                          <span>
                            <Checked /> {label}
                          </span>
                        ) : (
                          <span>
                            <NotChecked /> {label}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ))
              : addonToDisplay.map(({ label, key, value }) => (
                  <div className={css.pckgRow} key={key}>
                    {addonsForCustom && (
                      <div className={classNames(css.pckg, css.iconContainer)}>
                        {addonsForCustom?.includes(key) ? (
                          <span>
                            <Checked /> {label}
                          </span>
                        ) : (
                          <span>
                            <NotChecked /> {label}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ))}

            <div className={css.dateConteiner}>
              {category === ANIMAL_CONTENT_CREATOR ? (
                <FieldDateInput
                  className={css.bookingDates}
                  name="selectedbookingDates"
                  id="selectedbookingDates"
                  // name="bookingDates"
                  // id="bookingDates"
                  placeholderText={intl.formatDate(
                    values.bookingDates?.startDate || TODAY,
                    dateFormattingOptions
                  )}
                  label={selectDateLabel}
                  onChange={value => singleDateChange(value, form, props)}
                  initialVisibleMonth={initialVisibleMonth(startDate || startOfToday, timeZone)}
                  navNext={
                    <Next
                      currentMonth={currentMonth}
                      timeZone={timeZone}
                      dayCountAvailableForBooking={dayCountAvailableForBooking}
                    />
                  }
                  navPrev={<Prev currentMonth={currentMonth} timeZone={timeZone} />}
                  onPrevMonthClick={() => {
                    setCurrentMonth(prevMonth => prevMonthFn(prevMonth, timeZone));
                    onMonthClick(prevMonthFn);
                  }}
                  onNextMonthClick={() => {
                    setCurrentMonth(prevMonth => nextMonthFn(prevMonth, timeZone));
                    onMonthClick(nextMonthFn);
                  }}
                  isDayBlocked={isDayBlocked()}
                  isOutsideRange={isOutsideRange()}
                  isBlockedBetween={isBlockedBetween(monthlyTimeSlots, timeZone)}
                  enableOutsideDays={false}
                />
              ) : (
                <FieldDateRangeInput
                  className={css.bookingDates}
                  name="bookingDates"
                  isDaily={lineItemUnitType === LINE_ITEM_DAY}
                  startDateId={`${formId}.bookingStartDate`}
                  startDateLabel={intl.formatMessage({
                    id: 'BookingDatesForm.bookingStartTitle',
                  })}
                  startDatePlaceholderText={startDatePlaceholderText}
                  endDateId={`${formId}.bookingEndDate`}
                  endDateLabel={intl.formatMessage({
                    id: 'BookingDatesForm.bookingEndTitle',
                  })}
                  endDateTooltip={
                    <>
                      <a data-tooltip-id="endTip" data-tooltip-content={tooltipMsg}>
                        <InfoIcon />
                      </a>
                      <Tooltip id="endTip" className={css.tooltip} />
                    </>
                  }
                  endDatePlaceholderText={endDatePlaceholderText}
                  focusedInput={focusedInput}
                  onFocusedInputChange={onFocusedInputChange}
                  format={v => {
                    const { startDate, endDate } = v || {};
                    // Format the Final Form field's value for the DateRangeInput
                    // DateRangeInput operates on local time zone, but the form uses listing's time zone
                    const formattedStart = startDate
                      ? timeOfDayFromTimeZoneToLocal(startDate, timeZone)
                      : startDate;
                    const formattedEnd = endDate
                      ? timeOfDayFromTimeZoneToLocal(endDate, timeZone)
                      : endDate;
                    return v ? { startDate: formattedStart, endDate: formattedEnd } : v;
                  }}
                  parse={v => {
                    const { startDate, endDate } = v || {};
                    // Parse the DateRangeInput's value (local noon) for the Final Form
                    // The form expects listing's time zone and start of day aka 00:00
                    const parsedStart = startDate
                      ? getStartOf(
                          timeOfDayFromLocalToTimeZone(startDate, timeZone),
                          'day',
                          timeZone
                        )
                      : startDate;
                    const parsedEnd = endDate
                      ? getStartOf(timeOfDayFromLocalToTimeZone(endDate, timeZone), 'day', timeZone)
                      : endDate;
                    return v ? { startDate: parsedStart, endDate: parsedEnd } : v;
                  }}
                  useMobileMargins
                  validate={composeValidators(
                    required(
                      intl.formatMessage({
                        id: 'BookingDatesForm.requiredDate',
                      })
                    ),
                    bookingDatesRequired(startDateErrorMessage, endDateErrorMessage)
                  )}
                  initialVisibleMonth={initialVisibleMonth(startDate || startOfToday, timeZone)}
                  navNext={
                    <Next
                      currentMonth={currentMonth}
                      timeZone={timeZone}
                      dayCountAvailableForBooking={dayCountAvailableForBooking}
                    />
                  }
                  navPrev={<Prev currentMonth={currentMonth} timeZone={timeZone} />}
                  onPrevMonthClick={() => {
                    setCurrentMonth(prevMonth => prevMonthFn(prevMonth, timeZone));
                    onMonthClick(prevMonthFn);
                  }}
                  onNextMonthClick={() => {
                    setCurrentMonth(prevMonth => nextMonthFn(prevMonth, timeZone));
                    onMonthClick(nextMonthFn);
                  }}
                  isDayBlocked={isDayBlocked}
                  isOutsideRange={isOutsideRange}
                  isBlockedBetween={isBlockedBetween(monthlyTimeSlots, timeZone)}
                  disabled={fetchLineItemsInProgress}
                  onClose={event =>
                    setCurrentMonth(getStartOf(event?.startDate ?? startOfToday, 'month', timeZone))
                  }
                />
              )}
            </div>

            {/* <div className={css.infoContainer}>
              <div className={css.info}>
                Animal Talent is booked on a day rate, select the date your job will be completed on
                or the number of days the job will be performed. IE. 2 days selected will be charged
                double the rate.
              </div>
            </div> */}

            <div className={css.extraContainer}>
              {shouldShowExtraAddon
                ? extraAddons
                    .filter(({ price }) => price.amount)
                    .map(({ price, title }) => (
                      <div className={css.extraField} key={title}>
                        <FieldCheckbox
                          id={title}
                          key={title}
                          name={'extraAddOn'}
                          value={title}
                          label={title}
                        />

                        <span>
                          {
                            priceData(new Money(price.amount, price.currency), price.currency, intl)
                              .formattedPrice
                          }
                        </span>
                      </div>
                    ))
                : extraAddOnForCustom
                    .filter(({ price }) => price.amount)
                    .map(({ price, title }) => (
                      <div className={css.extraField} key={title}>
                        <FieldCheckbox
                          id={title}
                          key={title}
                          name={'extraAddOnForCustom'}
                          value={title}
                          label={title}
                        />

                        <span>
                          {
                            priceData(new Money(price.amount, price.currency), price.currency, intl)
                              .formattedPrice
                          }
                        </span>
                      </div>
                    ))}
            </div>

            {showEstimatedBreakdown ? (
              <div className={css.priceBreakdownContainer}>
                <H6 as="h3" className={css.bookingBreakdownTitle}>
                  <FormattedMessage id="BookingDatesForm.priceBreakdownTitle" />
                </H6>
                <hr className={css.totalDivider} />
                <EstimatedCustomerBreakdownMaybe
                  breakdownData={breakdownData}
                  lineItems={lineItems}
                  timeZone={timeZone}
                  currency={unitPrice.currency}
                  marketplaceName={marketplaceName}
                  processName={BOOKING_PROCESS_NAME}
                  listing={listing}
                />
              </div>
            ) : null}
            {fetchLineItemsError ? (
              <span className={css.sideBarError}>
                <FormattedMessage id="BookingDatesForm.fetchLineItemsError" />
              </span>
            ) : null}

            {/* <div className={css.infoContainer}>
              <div className={css.info}>
                Select the date your job will be performed or completed on.
              </div>
              <div className={css.info}>
                Payment is issued upon job completion or 14 days after set date.
              </div>
            </div> */}

            {shouldShowCustomQuoteRequestModal && typeof onSubmitCustomQuoteRequest === 'function' && (
              <Modal
                id="BookingDatesForm.customRequest"
                contentClassName={css.inquiryModalContent}
                isOpen={isCustomQuoteRequestFormOpen}
                onClose={() => setIsCustomQuoteRequestFormOpen(false)}
                usePortal
                onManageDisableScrolling={onManageDisableScrolling}
              >
                <CustomRequestForm
                  className={css.inquiryForm}
                  submitButtonWrapperClassName={css.inquirySubmitButtonWrapper}
                  listingTitle={listingTitle || ''}
                  authorDisplayName={authorDisplayName}
                  customQuoteRequestError={customQuoteRequestError}
                  onSubmit={onSubmitCustomQuoteRequest}
                  inProgress={customQuoteRequestInProgress}
                />
              </Modal>
            )}

            <div className={css.submitButton}>
              <PrimaryButton type="submit" inProgress={fetchLineItemsInProgress}>
                <FormattedMessage id="BookingDatesForm.requestToBook" />
              </PrimaryButton>

              {shouldShowCustomQuoteRequestModal && (
                <div className={css.customRequestBtn}>
                  <SecondaryButton
                    type="button"
                    onClick={() => {
                      if (!isAuthenticated) {
                        return history.push('/login', { from: location.pathname });
                      }
                      setIsCustomQuoteRequestFormOpen(true);
                    }}
                  >
                    <FormattedMessage id="BookingDatesForm.customRequest" />
                  </SecondaryButton>
                </div>
              )}
            </div>
            <p className={css.finePrint}>
              <FormattedMessage
                id={
                  isOwnListing
                    ? 'BookingDatesForm.ownListing'
                    : 'BookingDatesForm.youWontBeChargedInfo'
                }
              />
            </p>
          </Form>
        );
      }}
    />
  );
};

BookingDatesFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  price: null,
  isOwnListing: false,
  startDatePlaceholder: null,
  endDatePlaceholder: null,
  lineItems: null,
  fetchLineItemsError: null,
  monthlyTimeSlots: null,
};

BookingDatesFormComponent.propTypes = {
  rootClassName: string,
  className: string,

  marketplaceName: string.isRequired,
  lineItemUnitType: propTypes.lineItemUnitType.isRequired,
  price: propTypes.money,
  isOwnListing: bool,
  monthlyTimeSlots: object,
  onFetchTimeSlots: func.isRequired,

  onFetchTransactionLineItems: func.isRequired,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,

  // from injectIntl
  intl: intlShape.isRequired,

  // for tests
  startDatePlaceholder: string,
  endDatePlaceholder: string,
  dayCountAvailableForBooking: number.isRequired,
};

const BookingDatesForm = compose(injectIntl)(BookingDatesFormComponent);
BookingDatesForm.displayName = 'BookingDatesForm';

export default BookingDatesForm;
